import React, { useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    Paper,
    MenuItem,
    FormControlLabel,
    Switch,
    IconButton,
    Divider,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import Styles from "./DataAddScreen.module.scss";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import {
    getAllLines,
    getAllInterestTypes,
    getAllUsers,
    saveBulkEntry,
    getAllAccounts,
    getCollectionsByAccount,
    updateBulkEntry,
} from "../../services/finance.service";

const DataAddScreen = () => {
    const navigate = useNavigate();
    const [generatedDates, setGeneratedDates] = useState([]);
    const [lines, setLines] = useState([]);
    const [interestTypes, setInterestTypes] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [submitError, setSubmitError] = useState(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);

    // Edit mode state
    const [isEditMode, setIsEditMode] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [allAccounts, setAllAccounts] = useState([]);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [selectedAccountId, setSelectedAccountId] = useState(""); // _id of AccountDetails
    const [editingAccountDbId, setEditingAccountDbId] = useState(null); // the _id used in PUT
    const [editOriginalCount, setEditOriginalCount] = useState(0);       // how many days were originally loaded

    const formik = useFormik({
        initialValues: {
            customerName: "",
            employee: "",
            line: "",
            fromDate: new Date().toISOString().split("T")[0],
            toDate: new Date().toISOString().split("T")[0],
            isActive: true,
            amount: "",
            interest: "",
            manualInterest: "",
            serviceAmount: "",
            address: "",
            count: 1,
            accountNumber: "",
        },
        validationSchema: Yup.object({
            customerName: Yup.string().required("Required"),
            employee: Yup.string().required("Required"),
            line: Yup.string().required("Required"),
            fromDate: Yup.date().required("Required"),
            toDate: Yup.date().required("Required").min(Yup.ref("fromDate"), "To Date cannot be before From Date"),
            amount: Yup.number().required("Required").positive(),
            interest: Yup.string().required("Required"),
            address: Yup.string().optional(),
            manualInterest: Yup.number().when("interest", {
                is: "other",
                then: (schema) => schema.required("Required").positive(),
                otherwise: (schema) => schema.optional(),
            }),
            serviceAmount: Yup.number().required("Required"),
            count: Yup.number().optional(),
            accountNumber: Yup.number().optional(),
        }),
        onSubmit: async (values) => {
            setSubmitError(null);
            setSubmitSuccess(false);
            try {
                // Check if accountNumber already exists for another account
                const requestedNum = String(values.accountNumber).trim();
                if (requestedNum) {
                    const isDuplicate = allAccounts.some(acc =>
                        String(acc.accountDetails?.accountNumber).trim() === requestedNum &&
                        acc._id !== editingAccountDbId
                    );

                    if (isDuplicate) {
                        setSubmitError(`Account number ${requestedNum} already exists. Please use a unique number.`);
                        return;
                    }
                }

                if (isEditMode && editingAccountDbId) {
                    // --- EDIT MODE ---
                    const payload = {
                        customerName: values.customerName,
                        employee: values.employee,
                        line: values.line,
                        isActive: values.isActive,
                        address: values.address,
                        amount: values.amount,
                        interest: values.interest,
                        manualInterest: values.manualInterest,
                        serviceAmount: values.serviceAmount,
                        accountNumber: values.accountNumber, // Allow updating account number
                        dayStatuses: generatedDates.map((d) => ({
                            _id: d._id || null,
                            date: d.rawDate,
                            status: d.status,
                            amount: d.amount,
                        })),
                    };
                    await updateBulkEntry(editingAccountDbId, payload);
                    setSubmitSuccess(true);
                    // Refresh account list to reflect changes
                    const freshAccs = await getAllAccounts();
                    setAllAccounts(Array.isArray(freshAccs) ? freshAccs : freshAccs?.data || []);
                    setTimeout(() => setSubmitSuccess(false), 5000);
                } else {
                    // --- CREATE MODE ---
                    const { fromDate, toDate, ...otherValues } = values;
                    const payload = {
                        ...otherValues,
                        date: fromDate,
                        dayStatuses: generatedDates.map((d) => ({
                            date: d.rawDate,
                            status: d.status,
                            amount: d.amount,
                        })),
                    };
                    const res = await saveBulkEntry(payload);
                    if (res) {
                        setSubmitSuccess(true);
                        formik.resetForm();
                        applyDefaults(employees, interestTypes, lines);
                        setIsEditMode(false);
                        setEditingAccountDbId(null);
                        // Refresh account list
                        const freshAccs = await getAllAccounts();
                        setAllAccounts(Array.isArray(freshAccs) ? freshAccs : freshAccs?.data || []);
                        setTimeout(() => setSubmitSuccess(false), 5000);
                    }
                }
            } catch (error) {
                console.error("Save failed:", error);
                setSubmitError(error.message || "Unknown error");
            }
        },
    });

    // Fetch reference data from backend
    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const [linesRes, interestRes, usersRes, accountsRes] = await Promise.all([
                    getAllLines(),
                    getAllInterestTypes(),
                    getAllUsers(),
                    getAllAccounts(),
                ]);

                const lineData = Array.isArray(linesRes) ? linesRes : linesRes?.data || [];
                const interestData = Array.isArray(interestRes) ? interestRes : interestRes?.data || [];
                const allUsers = Array.isArray(usersRes) ? usersRes.data || usersRes : usersRes?.data || [];
                const employeeData = Array.isArray(allUsers) ? allUsers.filter((u) => u.role === "user") : [];
                const accountsData = Array.isArray(accountsRes) ? accountsRes : accountsRes?.data || [];

                setLines(lineData);
                setInterestTypes(interestData);
                setEmployees(employeeData);
                setAllAccounts(accountsData);
                applyDefaults(employeeData, interestData, lineData);
            } catch (error) {
                console.error("Error fetching form data:", error);
            }
        };
        fetchData();
    }, []);

    // Helper to apply default selections
    const applyDefaults = (empData, intData, lData) => {
        if (empData && empData.length > 0) formik.setFieldValue("employee", empData[0]._id);
        if (intData && intData.length > 0) formik.setFieldValue("interest", intData[0]._id);
        if (lData && lData.length > 0) {
            const firstLine = lData[0];
            formik.setFieldValue("line", firstLine._id);
            if (firstLine.interestTypeId?._id) formik.setFieldValue("interest", firstLine.interestTypeId._id);
            formik.setFieldValue("serviceAmount", firstLine.isServiceCharge ? firstLine.serviceAmount || 0 : 0);
        }
    };

    // COUNT CALCULATION — CREATE mode ONLY
    React.useEffect(() => {
        if (isEditMode) return;
        if (formik.values.fromDate && formik.values.toDate) {
            const start = new Date(formik.values.fromDate);
            const end = new Date(formik.values.toDate);
            const diffTime = end - start;
            formik.setFieldValue("count", diffTime >= 0 ? Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1 : 0);
        }
    }, [formik.values.fromDate, formik.values.toDate]); // eslint-disable-line

    // CREATE MODE: Generate full date rows from scratch
    React.useEffect(() => {
        if (isEditMode) return;
        if (formik.values.fromDate && formik.values.count > 0) {
            const dates = [];
            const startDate = new Date(formik.values.fromDate);
            const dailyAmount = (Number(formik.values.amount) || 0) * 0.01;
            for (let i = 1; i <= formik.values.count; i++) {
                const nextDate = new Date(startDate);
                nextDate.setDate(startDate.getDate() + (i - 1));
                dates.push({
                    id: i,
                    rawDate: toYMD(nextDate),
                    date: nextDate.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
                    status: "Received",
                    amount: dailyAmount,
                });
            }
            setGeneratedDates(dates);
        } else {
            setGeneratedDates([]);
        }
    }, [formik.values.fromDate, formik.values.count, formik.values.amount, isEditMode]); // eslint-disable-line

    // ── Helper: format a local Date to "YYYY-MM-DD" without timezone shift ──────
    const toYMD = (d) => {
        const yr = d.getFullYear();
        const mo = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${yr}-${mo}-${day}`;
    };

    // ── Helper: rebuild a row's date fields from a base Date + offset ───────────
    const makeDateRow = (baseDate, offsetDays, existingRow, dailyAmount) => {
        const d = new Date(baseDate);
        d.setDate(baseDate.getDate() + offsetDays);
        return {
            ...existingRow,
            id: offsetDays + 1,
            rawDate: toYMD(d),
            date: d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
        };
    };

    // ── Helper: create a fresh new row (NO _id – signals backend to delete old + create new) ──
    const makeNewRow = (baseDate, offsetDays, dailyAmount) => {
        const d = new Date(baseDate);
        d.setDate(baseDate.getDate() + offsetDays);
        return {
            id: offsetDays + 1,
            _id: null,           // no DB id → backend will delete all old and insert fresh
            rawDate: toYMD(d),
            date: d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
            status: "Received",
            amount: dailyAmount,
        };
    };

    // EDIT MODE — fromDate changed:
    //   • Shifts the whole timeline: keeps existing status/ids but updates dates
    const handleEditFromDateChange = (e) => {
        const newFromStr = e.target.value;
        if (!newFromStr) return;

        const newFrom = new Date(newFromStr);
        const count = generatedDates.length || 1;

        // Adjust toDate to maintain the same day count
        const newTo = new Date(newFrom);
        newTo.setDate(newFrom.getDate() + count - 1);
        const newToStr = toYMD(newTo);

        formik.setValues({ ...formik.values, fromDate: newFromStr, toDate: newToStr });

        setGeneratedDates(prev => prev.map((row, i) => {
            const d = new Date(newFrom);
            d.setDate(newFrom.getDate() + i);
            return {
                ...row,
                rawDate: toYMD(d),
                date: d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
            };
        }));
    };

    // EDIT MODE — toDate changed (Extend/Trim tenure):
    //   • If extended: keeps existing data and appends fresh rows for new days
    //   • If trimmed: removes excess days from the end
    const handleEditToDateChange = (e) => {
        const newToStr = e.target.value;
        if (!newToStr) return;

        const fromStr = formik.values.fromDate;
        const start = new Date(fromStr);
        const end = new Date(newToStr);
        const diffTime = end - start;
        if (diffTime < 0) return;

        const newCount = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        const dailyAmount = (Number(formik.values.amount) || 0) * 0.01;

        formik.setFieldValue("toDate", newToStr);
        formik.setFieldValue("count", newCount);

        setGeneratedDates(prev => {
            if (newCount > prev.length) {
                // Extension: keep existing, append new ones
                const addedRows = [];
                for (let i = prev.length; i < newCount; i++) {
                    addedRows.push(makeNewRow(start, i, dailyAmount));
                }
                return [...prev, ...addedRows];
            } else if (newCount < prev.length) {
                // Trimming: remove from end
                return prev.slice(0, newCount);
            }
            return prev;
        });
    };


    // EDIT MODE — amount changed: recalculate per-day amounts directly (no effect needed)
    const handleEditAmountChange = (e) => {
        formik.handleChange(e);
        const newAmt = Number(e.target.value) || 0;
        const dailyAmount = newAmt * 0.01;
        setGeneratedDates((prev) =>
            prev.map((day) => ({ ...day, amount: day.status === "Not Received" ? 0 : dailyAmount }))
        );
    };


    // Toggle day status
    const toggleDayStatus = (id, newStatus) => {
        setGeneratedDates((prev) =>
            prev.map((day) => {
                if (day.id === id) {
                    const dailyAmount = (Number(formik.values.amount) || 0) * 0.01;
                    return { ...day, status: newStatus, amount: newStatus === "Not Received" ? 0 : dailyAmount };
                }
                return day;
            })
        );
    };

    // Manual amount change
    const handleDayAmountChange = (id, val) => {
        const numericVal = val === "" ? 0 : Number(val);
        setGeneratedDates((prev) => prev.map((day) => (day.id === id ? { ...day, amount: numericVal } : day)));
    };

    // ─── EDIT MODE HANDLERS ────────────────────────────────────────────────────

    const handleOpenEditDialog = async () => {
        setAccountsLoading(true);
        setEditDialogOpen(true);
        try {
            const res = await getAllAccounts();
            const data = Array.isArray(res) ? res : res?.data || [];
            setAllAccounts(data);
        } catch (e) {
            console.error("Failed to load accounts", e);
        } finally {
            setAccountsLoading(false);
        }
    };

    const handleSelectAccount = async (accountId) => {
        if (!accountId) return;
        setSelectedAccountId(accountId);

        // Find the account record from the already-loaded list
        const accountRecord = allAccounts.find((a) => a._id === accountId);
        if (!accountRecord) return;

        const details = accountRecord.accountDetails;

        try {
            // Fetch daily collections for this account
            const collectionsRes = await getCollectionsByAccount(accountId);
            const collections = Array.isArray(collectionsRes) ? collectionsRes : collectionsRes?.data || [];

            // Sort: primary = dayNumber ascending (Day 1 → Day N), fallback = createdAt ascending
            collections.sort((a, b) => {
                const dayDiff = (a.dayNumber || 0) - (b.dayNumber || 0);
                if (dayDiff !== 0) return dayDiff;
                return new Date(a.createdAt) - new Date(b.createdAt);
            });

            // Pre-fill form fields
            formik.setValues({
                customerName: accountRecord.name || details?.customerId?.name || "",
                employee: details?.createdBy?._id || details?.createdBy || "",
                line: details?.lineId || "",
                fromDate: collections.length > 0
                    ? new Date(collections[0].createdAt).toISOString().split("T")[0]
                    : new Date().toISOString().split("T")[0],
                toDate: collections.length > 0
                    ? new Date(collections[collections.length - 1].createdAt).toISOString().split("T")[0]
                    : new Date().toISOString().split("T")[0],
                isActive: accountRecord.status === "active",
                amount: details?.amount || "",
                interest: details?.interestTypeId?._id || details?.interestTypeId || "",
                manualInterest: "",
                serviceAmount: details?.serviceAmount || 0,
                address: accountRecord.address || details?.customerId?.address || "",
                count: collections.length || 1,
                accountNumber: details?.accountNumber || "",
            });

            // Build generatedDates from collections
            const rows = collections.map((col, idx) => {
                const d = new Date(col.createdAt);
                return {
                    id: idx + 1,
                    _id: col._id, // preserve DB _id for update payload
                    rawDate: d.toISOString().split("T")[0],
                    date: d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
                    status: col.status,
                    amount: col.amount || 0,
                };
            });
            setGeneratedDates(rows);
            setEditOriginalCount(rows.length); // remember the original count for deletion tracking

            setEditingAccountDbId(accountId);
            setIsEditMode(true);
            setEditDialogOpen(false);
        } catch (e) {
            console.error("Failed to load account details", e);
        }
    };

    const handleCancelEdit = () => {
        setIsEditMode(false);
        setEditingAccountDbId(null);
        setSelectedAccountId("");
        setEditOriginalCount(0);
        formik.resetForm();
        applyDefaults(employees, interestTypes, lines);
        setGeneratedDates([]);
    };

    // ─── SUMMARY ──────────────────────────────────────────────────────────────
    const totalEntered = generatedDates.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);
    const remainingToCollect = (Number(formik.values.amount) || 0) - totalEntered;
    // In edit mode: how many original DB rows are now trimmed away
    const deletedCount = isEditMode ? Math.max(0, editOriginalCount - generatedDates.length) : 0;

    return (
        <Box className={Styles.container}>
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/welcome")}
                        className={Styles.backButton}
                    >
                        Back to Welcome
                    </Button>

                    {/* EDIT BUTTON */}
                    <Button
                        startIcon={<EditIcon />}
                        variant={isEditMode ? "outlined" : "contained"}
                        color={isEditMode ? "warning" : "secondary"}
                        onClick={isEditMode ? handleCancelEdit : handleOpenEditDialog}
                        sx={{
                            borderRadius: "12px",
                            fontWeight: 700,
                            textTransform: "none",
                            px: 3,
                        }}
                    >
                        {isEditMode ? "Cancel Edit" : "Edit"}
                    </Button>

                    {isEditMode && (
                        <Typography
                            sx={{
                                background: "linear-gradient(135deg,#f59e0b,#ef4444)",
                                color: "#fff",
                                px: 2,
                                py: 0.5,
                                borderRadius: "20px",
                                fontSize: "0.8rem",
                                fontWeight: 700,
                            }}
                        >
                            ✏️ Editing Account #{formik.values.accountNumber}
                        </Typography>
                    )}
                </Box>
            </motion.div>

            <Grid container spacing={3} justifyContent="center" sx={{ maxWidth: 1800 }}>
                {/* Top Section: Compact Form */}
                <Grid item xs={12}>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        <Paper className={Styles.formPaper} elevation={0}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                                <Box>
                                    <Typography variant="h4" className={Styles.title}>
                                        Customer <span className={Styles.highlight}>Details</span>
                                    </Typography>
                                    <Typography className={Styles.subtitle} sx={{ mb: "0 !important" }}>
                                        {isEditMode
                                            ? "Edit existing customer entry data below."
                                            : "Fill in the data to create a new user entry."}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 200 }}>
                                    {submitSuccess && (
                                        <Alert severity="success" sx={{ mb: 1 }}>
                                            {isEditMode ? "Entry Updated Successfully!" : "Bulk Entry Saved Successfully!"}
                                        </Alert>
                                    )}
                                    {submitError && (
                                        <Alert severity="error" sx={{ mb: 1 }}>
                                            {submitError}
                                        </Alert>
                                    )}
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        className={Styles.submitButton}
                                        onClick={formik.handleSubmit}
                                        sx={{
                                            width: "100%",
                                            background: isEditMode
                                                ? "linear-gradient(135deg,#f59e0b,#ef4444)"
                                                : undefined,
                                        }}
                                    >
                                        {isEditMode ? "Update Entry" : "Save User Entry"}
                                    </Button>
                                </Box>
                            </Box>

                            <form onSubmit={formik.handleSubmit} className={Styles.form}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} md={2}>
                                        <Typography className={Styles.label}>Account Number</Typography>
                                        <TextField
                                            fullWidth
                                            name="accountNumber"
                                            type="number"
                                            placeholder="Auto-gen if empty"
                                            value={formik.values.accountNumber}
                                            onChange={formik.handleChange}
                                            error={formik.touched.accountNumber && Boolean(formik.errors.accountNumber)}
                                            size="small"
                                            className={Styles.inputField}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography className={Styles.label}>Customer Name</Typography>
                                        <TextField
                                            fullWidth
                                            name="customerName"
                                            placeholder="Enter Name"
                                            value={formik.values.customerName}
                                            onChange={formik.handleChange}
                                            error={formik.touched.customerName && Boolean(formik.errors.customerName)}
                                            size="small"
                                            className={Styles.inputField}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography className={Styles.label}>Address</Typography>
                                        <TextField
                                            fullWidth
                                            name="address"
                                            placeholder="Enter Address"
                                            value={formik.values.address}
                                            onChange={formik.handleChange}
                                            error={formik.touched.address && Boolean(formik.errors.address)}
                                            size="small"
                                            className={Styles.inputField}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={2}>
                                        <Typography className={Styles.label}>Employee</Typography>
                                        <TextField
                                            select
                                            fullWidth
                                            name="employee"
                                            value={formik.values.employee}
                                            onChange={formik.handleChange}
                                            error={formik.touched.employee && Boolean(formik.errors.employee)}
                                            size="small"
                                            className={Styles.inputField}
                                        >
                                            <MenuItem value="">Select Employee</MenuItem>
                                            {employees.map((emp) => (
                                                <MenuItem key={emp._id} value={emp._id}>
                                                    {emp.name || emp.username}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={2}>
                                        <Typography className={Styles.label}>Line</Typography>
                                        <TextField
                                            select
                                            fullWidth
                                            name="line"
                                            value={formik.values.line}
                                            onChange={(e) => {
                                                const lineId = e.target.value;
                                                formik.setFieldValue("line", lineId);
                                                const selectedLine = lines.find((l) => l._id === lineId);
                                                if (selectedLine) {
                                                    if (selectedLine.interestTypeId?._id)
                                                        formik.setFieldValue("interest", selectedLine.interestTypeId._id);
                                                    formik.setFieldValue(
                                                        "serviceAmount",
                                                        selectedLine.isServiceCharge ? selectedLine.serviceAmount || 0 : 0
                                                    );
                                                }
                                            }}
                                            error={formik.touched.line && Boolean(formik.errors.line)}
                                            size="small"
                                            className={Styles.inputField}
                                        >
                                            <MenuItem value="">Select Line</MenuItem>
                                            {lines.map((l) => (
                                                <MenuItem key={l._id} value={l._id}>
                                                    {l.lineName}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={2}>
                                        <Typography className={Styles.label}>From Date</Typography>
                                        <TextField
                                            fullWidth
                                            name="fromDate"
                                            type="date"
                                            value={formik.values.fromDate}
                                            onChange={isEditMode ? handleEditFromDateChange : formik.handleChange}
                                            size="small"
                                            error={formik.touched.fromDate && Boolean(formik.errors.fromDate)}
                                            className={Styles.inputField}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={2}>
                                        <Typography className={Styles.label}>To Date</Typography>
                                        <TextField
                                            fullWidth
                                            name="toDate"
                                            type="date"
                                            value={formik.values.toDate}
                                            onChange={isEditMode ? handleEditToDateChange : formik.handleChange}
                                            size="small"
                                            error={formik.touched.toDate && Boolean(formik.errors.toDate)}
                                            className={Styles.inputField}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={2}>
                                        <Typography className={Styles.label}>Amount</Typography>
                                        <TextField
                                            fullWidth
                                            name="amount"
                                            type="number"
                                            placeholder="0.00"
                                            value={formik.values.amount}
                                            onChange={isEditMode ? handleEditAmountChange : formik.handleChange}
                                            size="small"
                                            className={Styles.inputField}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={2}>
                                        <Typography className={Styles.label}>Interest</Typography>
                                        <TextField
                                            select
                                            fullWidth
                                            name="interest"
                                            value={formik.values.interest}
                                            onChange={formik.handleChange}
                                            size="small"
                                            className={Styles.inputField}
                                        >
                                            <MenuItem value="">Select Type</MenuItem>
                                            {interestTypes.map((type) => (
                                                <MenuItem key={type._id} value={type._id}>
                                                    {type.name} ({type.valuePer1000})
                                                </MenuItem>
                                            ))}
                                            <MenuItem value="other">Manual</MenuItem>
                                        </TextField>
                                    </Grid>

                                    {formik.values.interest === "other" && (
                                        <Grid item xs={12} sm={6} md={2}>
                                            <Typography className={Styles.label}>Manual Int.</Typography>
                                            <TextField
                                                fullWidth
                                                name="manualInterest"
                                                type="number"
                                                value={formik.values.manualInterest}
                                                onChange={formik.handleChange}
                                                size="small"
                                                className={Styles.inputField}
                                            />
                                        </Grid>
                                    )}

                                    <Grid item xs={12} sm={6} md={2}>
                                        <Typography className={Styles.label}>Service Amt</Typography>
                                        <TextField
                                            fullWidth
                                            name="serviceAmount"
                                            type="number"
                                            value={formik.values.serviceAmount}
                                            onChange={formik.handleChange}
                                            size="small"
                                            className={Styles.inputField}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={1}>
                                        <Typography className={Styles.label}>Days</Typography>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                background: "#f8fafc",
                                                p: "6px 12px",
                                                borderRadius: "10px",
                                                border: "1px solid #e2e8f0",
                                                textAlign: "center",
                                                fontWeight: 800,
                                                color: "#4f46e5",
                                            }}
                                        >
                                            {formik.values.count}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={2} sx={{ display: "flex", alignItems: "flex-end", pb: "4px" }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={formik.values.isActive}
                                                    onChange={(e) => formik.setFieldValue("isActive", e.target.checked)}
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Typography className={Styles.label} sx={{ mb: "0 !important", ml: 1 }}>
                                                    Active User
                                                </Typography>
                                            }
                                        />
                                    </Grid>
                                </Grid>
                            </form>
                        </Paper>
                    </motion.div>
                </Grid>

                {/* Bottom Section: Timeline Grid */}
                <Grid item xs={12}>
                    <AnimatePresence>
                        {generatedDates.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 20 }}
                                transition={{ duration: 0.5 }}
                            >
                                <Paper className={Styles.dateListPaper} elevation={0}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            mb: 3,
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <Typography variant="h6" className={Styles.listTitle} sx={{ mb: 0 }}>
                                            {isEditMode ? (
                                                <>
                                                    Edit <span className={Styles.highlight}>Timeline Grid</span>
                                                </>
                                            ) : (
                                                <>
                                                    Projected <span className={Styles.highlight}>Timeline Grid</span>
                                                </>
                                            )}
                                        </Typography>
                                        {/* Deletion warning chip */}
                                        {deletedCount > 0 && (
                                            <Typography sx={{
                                                background: "rgba(239,68,68,0.1)",
                                                color: "#ef4444",
                                                border: "1px solid #ef4444",
                                                borderRadius: "20px",
                                                px: 2, py: 0.4,
                                                fontSize: "0.78rem",
                                                fontWeight: 700,
                                            }}>
                                                ⚠️ {deletedCount} day{deletedCount > 1 ? "s" : ""} will be deleted on save
                                            </Typography>
                                        )}

                                        <Box
                                            sx={{
                                                display: "flex",
                                                gap: 3,
                                                background: "rgba(79, 70, 229, 0.05)",
                                                p: "10px 20px",
                                                borderRadius: "12px",
                                            }}
                                        >
                                            <Box>
                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: "#64748b", fontWeight: 700, display: "block" }}
                                                >
                                                    TOTAL ENTERED
                                                </Typography>
                                                <Typography variant="h6" sx={{ color: "#4f46e5", fontWeight: 800 }}>
                                                    ₹{totalEntered.toLocaleString("en-IN")}
                                                </Typography>
                                            </Box>
                                            <Divider orientation="vertical" flexItem sx={{ opacity: 0.1 }} />
                                            <Box>
                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: "#64748b", fontWeight: 700, display: "block" }}
                                                >
                                                    REMAINING
                                                </Typography>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        color: remainingToCollect === 0 ? "#10b981" : "#ef4444",
                                                        fontWeight: 800,
                                                    }}
                                                >
                                                    ₹{remainingToCollect.toLocaleString("en-IN")}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    <Box className={Styles.dateScrollList}>
                                        {(() => {
                                            let runningRemaining = Number(formik.values.amount) || 0;
                                            return generatedDates.map((item, index) => {
                                                runningRemaining -= Number(item.amount) || 0;
                                                const currentDayBalance = runningRemaining;
                                                return (
                                                    <motion.div
                                                        key={item.id}
                                                        initial={{ opacity: 0, scale: 0.9 }}
                                                        animate={{ opacity: 1, scale: 1 }}
                                                        transition={{ delay: index * 0.01 }}
                                                        className={Styles.dateItem}
                                                        style={{
                                                            borderLeft: `4px solid ${item.status === "Received" ? "#10b981" : "#ef4444"}`,
                                                            opacity: item.status === "Not Received" ? 0.8 : 1,
                                                        }}
                                                    >
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                                                            <Typography className={Styles.dateText}>
                                                                Day {item.id}
                                                                <span>{item.date}</span>
                                                                <span className={Styles.balanceText}>
                                                                    Balance: ₹{currentDayBalance.toLocaleString("en-IN")}
                                                                </span>
                                                            </Typography>

                                                            <TextField
                                                                id={`day-input-${item.id}`}
                                                                size="small"
                                                                type="number"
                                                                value={item.amount === 0 ? "" : item.amount}
                                                                onChange={(e) => handleDayAmountChange(item.id, e.target.value)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "Enter") {
                                                                        e.preventDefault();
                                                                        const nextInput = document.getElementById(
                                                                            `day-input-${item.id + 1}`
                                                                        );
                                                                        if (nextInput) nextInput.focus();
                                                                    }
                                                                }}
                                                                className={Styles.dayAmountInput}
                                                                InputProps={{
                                                                    startAdornment: (
                                                                        <Typography
                                                                            sx={{ mr: 0.5, fontSize: "0.8rem", color: "#94a3b8" }}
                                                                        >
                                                                            ₹
                                                                        </Typography>
                                                                    ),
                                                                }}
                                                                placeholder="0"
                                                            />
                                                        </Box>

                                                        <Box className={Styles.statusIcons}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => toggleDayStatus(item.id, "Received")}
                                                                sx={{
                                                                    backgroundColor:
                                                                        item.status === "Received"
                                                                            ? "rgba(16, 185, 129, 0.15)"
                                                                            : "transparent",
                                                                    border:
                                                                        item.status === "Received"
                                                                            ? "2px solid #10b981"
                                                                            : "2px solid #efefef",
                                                                    p: "4px",
                                                                }}
                                                            >
                                                                <CheckCircleIcon
                                                                    sx={{
                                                                        color:
                                                                            item.status === "Received" ? "#10b981" : "#e2e8f0",
                                                                        fontSize: "1rem",
                                                                    }}
                                                                />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => toggleDayStatus(item.id, "Not Received")}
                                                                sx={{
                                                                    backgroundColor:
                                                                        item.status === "Not Received"
                                                                            ? "rgba(239, 68, 68, 0.15)"
                                                                            : "transparent",
                                                                    border:
                                                                        item.status === "Not Received"
                                                                            ? "2px solid #ef4444"
                                                                            : "2px solid #efefef",
                                                                    p: "4px",
                                                                }}
                                                            >
                                                                <CancelIcon
                                                                    sx={{
                                                                        color:
                                                                            item.status === "Not Received"
                                                                                ? "#ef4444"
                                                                                : "#e2e8f0",
                                                                        fontSize: "1rem",
                                                                    }}
                                                                />
                                                            </IconButton>
                                                        </Box>
                                                    </motion.div>
                                                );
                                            });
                                        })()}
                                    </Box>
                                </Paper>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </Grid>
            </Grid>

            {/* ── Edit Account Selection Dialog ── */}
            <Dialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                PaperProps={{
                    sx: {
                        borderRadius: "20px",
                        minWidth: 360,
                        p: 1,
                        background: "linear-gradient(135deg,#1e1b4b 0%,#312e81 100%)",
                        color: "#fff",
                    },
                }}
            >
                <DialogTitle sx={{ fontWeight: 800, fontSize: "1.2rem", color: "#c7d2fe" }}>
                    🔍 Select Account to Edit
                </DialogTitle>
                <DialogContent>
                    {accountsLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <CircularProgress sx={{ color: "#818cf8" }} />
                        </Box>
                    ) : (
                        <TextField
                            select
                            fullWidth
                            label="Account Number"
                            value={selectedAccountId}
                            onChange={(e) => setSelectedAccountId(e.target.value)}
                            sx={{
                                mt: 1,
                                "& .MuiInputLabel-root": { color: "#a5b4fc" },
                                "& .MuiOutlinedInput-root": {
                                    color: "#fff",
                                    "& fieldset": { borderColor: "#6366f1" },
                                    "&:hover fieldset": { borderColor: "#818cf8" },
                                    "&.Mui-focused fieldset": { borderColor: "#a5b4fc" },
                                },
                                "& .MuiSvgIcon-root": { color: "#a5b4fc" },
                            }}
                            SelectProps={{
                                MenuProps: {
                                    PaperProps: {
                                        sx: {
                                            background: "#1e1b4b",
                                            color: "#e0e7ff",
                                            maxHeight: 300,
                                        },
                                    },
                                },
                            }}
                        >
                            <MenuItem value="">-- Select Account --</MenuItem>
                            {allAccounts.map((acc) => (
                                <MenuItem key={acc._id} value={acc._id}>
                                    #{acc.accountDetails?.accountNumber} — {acc.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => setEditDialogOpen(false)}
                        sx={{ color: "#94a3b8", textTransform: "none" }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!selectedAccountId}
                        onClick={() => handleSelectAccount(selectedAccountId)}
                        sx={{
                            background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
                            borderRadius: "12px",
                            textTransform: "none",
                            fontWeight: 700,
                            px: 3,
                        }}
                    >
                        Load Account
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DataAddScreen;
